import React, { useState, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Grid,
  useTheme,
  Tooltip,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import { Order, TableProps } from './playerTable.interface';
import { stableSort, getComparator, createData } from './playerTable.helper';
import { PlayerTablePagination } from './playerTablePagination';
import { PlayerTableHead } from './playerTableHead';
import { PlayerTableToolbar } from './playerTableToolbar';
import { dateTime } from '../../../time/datetime';
import { ConneqtStatusCell } from './conneqtStatusCell';
import { PlayerClearStatusCell } from './playerClearStatusCell';
import { AccountTypes } from '../../../components/changeUser/accountTypes.enum';
import { PlayerDataTableList } from '../../../state/reduxRepositories/players/player.interfaces';
import { getRouteByAccountType } from '../../../routes/getRouteByAccountType';

export function PlayerListTable({
  conneqtOnAddPlayerDomain,
  playerDomain,
  hidePlayerListActions,
  selectedPlayers,
  onAddPlayerOnTeam,
  showSaveButton,
  allowPlayerSelection,
}: TableProps) {
  const [order, setOrder] = useState<Order>('asc');
  const [isSavingPlayer, setSavingPlayer] = useState(false);
  const [orderBy, setOrderBy] = useState<string>('playerName');
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [nameFilter, setNameFilter] = useState('');
  const [rows, setRows] = useState<PlayerDataTableList[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [height, setHeight] = useState<string>('');
  const { t } = useTranslation();
  const [tableMessage, setTableMessage] = useState(
    t('NO_PLAYERS_IN_YOUR_FAMILY'),
  );
  const tableData = playerDomain.usePlayerListDataSelector();
  const playerTemplateSelector =
    playerDomain.useDownloadPlayerTemplateSelector();
  const accountType = playerDomain.useAccountTypeSelector();
  const isSyncing = playerDomain.useIsSyncingSelector();

  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const tableContainerRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'));

  const handleRequestSort = (property: keyof PlayerDataTableList) => {
    const dataProperty = property === 'dob' ? 'rawDOB' : property;
    const isAsc = orderBy === dataProperty && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(dataProperty);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((row) => row.playerId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (playerId: string) => {
    if (!allowPlayerSelection) return;
    const selectedIndex = selected.indexOf(playerId);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, playerId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    const offsetHeight = tableContainerRef?.current
      ? tableContainerRef?.current.clientHeight
      : '100%';
    setHeight(`${offsetHeight}px`);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setHeight('100%');
  };

  const isSelected = (playerId: string) => selected.indexOf(playerId) !== -1;

  const createTableData: PlayerDataTableList[] = tableData.map(
    (item: PlayerDataTableList) =>
      createData(
        item.playerId,
        item.playerName,
        item.team || '',
        dateTime.format(item?.dob, t('common.datetime.FORMAT_SHORT_DATE')),
        item.dob || '',
        item.clearStatus,
        item.conneqtedStatus || false,
        item.guardianEmail || '',
      ),
  );

  const visibleRows = useMemo(
    () =>
      stableSort(rows as any, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, orderBy, page, rowsPerPage, rows],
  );

  const downloadPlayerTemplate = () => {
    if (playerTemplateSelector) {
      window.open(playerTemplateSelector, '_blank');
    }
  };

  useEffect(() => {
    if (selectedPlayers) {
      const combineSelectedPlayers = selectedPlayers.concat(selected);
      setSelected(
        combineSelectedPlayers.filter(
          (value, index) => combineSelectedPlayers.indexOf(value) === index,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayers]);

  useEffect(() => {
    if (nameFilter) {
      const filter = createTableData.filter((row: PlayerDataTableList) =>
        row.playerName.toLowerCase().includes(nameFilter.toLowerCase()),
      );
      setRows(filter);
      setTableMessage(t('PLAYER_DOES_NOT_EXIST'));
      const offsetHeight = tableContainerRef?.current
        ? tableContainerRef?.current.clientHeight
        : '100%';
      setHeight(`${offsetHeight}px`);
      setPage(0);
    } else {
      setRows(createTableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter, tableData, createTableData]);

  const handleViewPlayerClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseViewPlayerClick = () => {
    setAnchorEl(null);
  };

  const navigateToPlayerProfile = () => {
    if (accountType) {
      navigate(
        `/${getRouteByAccountType(
          accountType,
        )}/player-profile?selectedPlayerId=${selectedPlayerId}`,
      );
    }
  };

  const onSavePlayer = () => {
    if (onAddPlayerOnTeam) {
      onAddPlayerOnTeam(selected);
      setSavingPlayer(true);
    }
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Paper sx={{ mb: 2, maxWidth: '100vw' }}>
        <PlayerTableToolbar
          isSavingPlayer={isSavingPlayer}
          onSavePlayer={onSavePlayer}
          hidePlayerListActions={hidePlayerListActions}
          showSaveButton={showSaveButton}
          dispatchCreatePlayer={conneqtOnAddPlayerDomain.dispatchCreatePlayer}
          dispatchDownloadPlayerTemplate={
            playerDomain.dispatchDownloadPlayerTemplate
          }
          accountType={playerDomain.useAccountTypeSelector()}
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          onRequestSort={handleRequestSort}
          onDownloadPlayerTemplate={downloadPlayerTemplate}
          uploadDisabled={isSyncing}
        />
        {selected.length > 0 && (
          <Box
            sx={{
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.action.activatedOpacity,
              ),
            }}
          >
            <Typography
              sx={{ flex: '1 1 100%', minHeight: '50px', pt: 1.5 }}
              color="inherit"
              variant="subtitle1"
              component="div"
            >
              {selected.length} {t('SELECTED').toString()}
            </Typography>
          </Box>
        )}
        <TableContainer sx={{ pt: 2 }}>
          {rows.length <= 0 && (
            <Grid container justifyContent="center" direction="row">
              <Typography sx={{ pt: 3 }}>{tableMessage}</Typography>
            </Grid>
          )}

          {rows.length > 0 && (
            <Box component="div" sx={{ height }} ref={tableContainerRef}>
              <Table
                aria-labelledby={t('PLAYERS_LIST')}
                size="small"
                sx={{
                  minWidth: 650,
                  tableLayout: 'fixed',
                }}
              >
                <PlayerTableHead
                  allowPlayerSelection={allowPlayerSelection}
                  accountType={accountType}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rows.length}
                />

                <TableBody>
                  {visibleRows.map((row, index) => {
                    const isItemSelected = isSelected(row.playerId.toString());

                    const labelId = `enhanced-table-checkbox-${index}`;
                    const isConneqted = !!row.conneqtedStatus;
                    const clearedToPlay = !!row.clearStatus;
                    const guardianEmails = row.guardianEmail
                      ?.toString()
                      .split(',');
                    const uniqueGuardianEmails = guardianEmails.filter(
                      (mail, indexRow) =>
                        guardianEmails?.indexOf(mail) === indexRow,
                    );
                    const hasMultipleGuardians =
                      uniqueGuardianEmails.length > 1;
                    const guardianEmailDisplayValue = hasMultipleGuardians
                      ? `${uniqueGuardianEmails[0]} ,...`
                      : uniqueGuardianEmails.toString();
                    const { playerName } = row;
                    const strPlayerName = playerName as string;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.playerId}
                        selected={isItemSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        {allowPlayerSelection && (
                          <TableCell
                            onClick={() => handleClick(row.playerId.toString())}
                            sx={{
                              width: '50px',
                            }}
                            padding="none"
                          >
                            <Checkbox
                              data-testid={`allocateItem-${strPlayerName?.replace(
                                /\s/g,
                                '',
                              )}`}
                              sx={{ color: theme.palette.text.primary }}
                              checked={isItemSelected}
                              onClick={() => {
                                handleClick(row.playerId.toString());
                              }}
                              inputProps={{
                                'aria-labelledby': labelId,
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell
                          onClick={() => {
                            handleClick(row.playerId.toString());
                          }}
                          component="th"
                          id={labelId}
                          scope="row"
                          sx={{
                            position: 'sticky',
                            zIndex: 100,
                            background:
                              isSmall || isMedium || isLarge
                                ? theme.palette.primary.contrastText
                                : '',
                            left: 0,
                          }}
                        >
                          {row.playerName}
                        </TableCell>
                        {accountType === AccountTypes.Org && (
                          <TableCell
                            align="center"
                            onClick={() => {
                              handleClick(row.playerId.toString());
                            }}
                          >
                            {row.team}
                          </TableCell>
                        )}
                        <TableCell
                          align="center"
                          onClick={() => {
                            handleClick(row.playerId.toString());
                          }}
                        >
                          {row.dob}
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => {
                            handleClick(row.playerId.toString());
                          }}
                        >
                          <PlayerClearStatusCell
                            clearedToPlay={clearedToPlay}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => {
                            handleClick(row.playerId.toString());
                          }}
                        >
                          <ConneqtStatusCell isConneqted={isConneqted} />
                        </TableCell>
                        {accountType === AccountTypes.Org && (
                          <>
                            {hasMultipleGuardians ? (
                              <Tooltip
                                title={
                                  <>
                                    {uniqueGuardianEmails.map((e) => {
                                      return <div key={e}>{e}</div>;
                                    })}
                                  </>
                                }
                                placement="top"
                              >
                                <TableCell
                                  align="center"
                                  onClick={() =>
                                    handleClick(row.playerId.toString())
                                  }
                                  sx={{
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {guardianEmailDisplayValue}
                                </TableCell>
                              </Tooltip>
                            ) : (
                              <TableCell
                                align="center"
                                onClick={() => {
                                  handleClick(row.playerId.toString());
                                }}
                                sx={{
                                  wordWrap: 'break-word',
                                }}
                              >
                                {guardianEmailDisplayValue}
                              </TableCell>
                            )}
                          </>
                        )}
                        <TableCell
                          align="center"
                          data-testid={row.playerName}
                          onClick={() => {
                            setSelectedPlayerId(row.playerId.toString());
                          }}
                        >
                          <IconButton
                            id="view-button"
                            onClick={handleViewPlayerClick}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleCloseViewPlayerClick}
                            MenuListProps={{
                              'aria-labelledby': 'more-button',
                            }}
                          >
                            <MenuItem
                              key={row.playerId}
                              onClick={navigateToPlayerProfile}
                            >
                              {t('PLAYER_PROFILE_VIEW')}
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </TableContainer>
        <PlayerTablePagination
          rowsPerPageOptions={[5, 10, 25]}
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
